/**
 * The API client.
 *
 * One place that knows about tokens, refresh and error shape, so no screen has
 * to. Three things it handles that would otherwise be repeated badly:
 *
 *  - Refresh on 401, ONCE, with concurrent requests sharing the same refresh
 *    rather than each firing their own and invalidating the others. Refresh
 *    tokens are single-use and a second use revokes the whole family, so a
 *    naive per-request refresh would log the host out during a rush.
 *  - Version conflicts (409) surfaced as a typed error the UI can explain,
 *    rather than a generic failure.
 *  - The venue id, which almost every staff endpoint requires.
 */

const ACCESS_KEY = 'yp.access';
const REFRESH_KEY = 'yp.refresh';
const VENUE_KEY = 'yp.venue';

export interface ApiErrorBody {
  error: {
    code: string;
    messageKey?: string;
    message?: string;
    details?: unknown;
    requestId?: string;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly messageKey: string | undefined;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(status: number, body: Partial<ApiErrorBody>) {
    super(body.error?.message ?? `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error?.code ?? 'unknown';
    this.messageKey = body.error?.messageKey;
    this.details = body.error?.details;
    this.requestId = body.error?.requestId;
  }

  /** Someone else edited this record first. The UI reloads and asks again. */
  get isVersionConflict(): boolean {
    return this.code === 'version_conflict';
  }

  /** The slot went, or the table was taken a moment ago. */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // Private browsing on the host stand's iPad.
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* nothing useful to do */
  }
}

export const tokens = {
  access: () => read(ACCESS_KEY),
  refresh: () => read(REFRESH_KEY),
  set(access: string, refresh: string) {
    write(ACCESS_KEY, access);
    write(REFRESH_KEY, refresh);
  },
  clear() {
    write(ACCESS_KEY, null);
    write(REFRESH_KEY, null);
  },
};

export const activeVenue = {
  get: () => read(VENUE_KEY),
  set: (id: string | null) => write(VENUE_KEY, id),
};

/** Called when refresh fails, so the app can send the user back to sign in. */
let onSignedOut: () => void = () => undefined;
export function setSignedOutHandler(handler: () => void): void {
  onSignedOut = handler;
}

/**
 * In-flight refresh, shared.
 *
 * Without this, a screen firing four queries at once on an expired token would
 * attempt four refreshes; the second would present an already-used token, which
 * the API treats as theft and responds to by revoking the entire family.
 */
let refreshing: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  const token = tokens.refresh();
  if (!token) return false;

  refreshing ??= (async () => {
    try {
      const res = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: token }),
      });
      if (!res.ok) return false;
      const body = (await res.json()) as { access_token: string; refresh_token: string };
      tokens.set(body.access_token, body.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so simultaneous callers all see this attempt.
      setTimeout(() => {
        refreshing = null;
      }, 0);
    }
  })();

  return refreshing;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Set for reservation creation, so a double submit produces one booking. */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, String(value));
  }
  return `${url.pathname}${url.search}`;
}

async function send<T>(path: string, options: RequestOptions, retry = true): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const access = tokens.access();
  if (access) headers.Authorization = `Bearer ${access}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;

  const res = await fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (res.status === 401 && retry && tokens.refresh()) {
    if (await refreshSession()) return send<T>(path, options, false);
    tokens.clear();
    onSignedOut();
  }

  if (res.status === 204) return undefined as T;

  const body = (await res.json().catch(() => ({}))) as Partial<ApiErrorBody> & T;
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) =>
    send<T>(path, { method: 'GET', ...(query ? { query } : {}) }),
  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    send<T>(path, { method: 'POST', body, ...options }),
  patch: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    send<T>(path, { method: 'PATCH', body, ...options }),
  put: <T>(path: string, body?: unknown) => send<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string, query?: RequestOptions['query']) =>
    send<T>(path, { method: 'DELETE', ...(query ? { query } : {}) }),
};

// --- response shapes --------------------------------------------------------

export interface VenueRole {
  venueId: string;
  role: string;
}

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  locale: 'en' | 'ar';
  venues: VenueRole[];
}

export interface FloorTable {
  id: string;
  label: string;
  room_id: string;
  seat_type: string;
  min_party: number;
  max_party: number;
  status: string;
  reservation: {
    id: string;
    booking_ref: string;
    party_size: number;
    starts_at: string;
    ends_at: string;
    status: string;
    occasion: string | null;
    guest_name: string | null;
    vip_level: number;
    allergies: string | null;
  } | null;
}

export interface FloorIssue {
  kind: string;
  severity: 'error' | 'warning';
  message: string;
  reservationIds: string[];
  tableIds: string[];
}

export interface FloorResponse {
  service_date: string;
  at: string;
  clock: string;
  tables: FloorTable[];
  issues: FloorIssue[];
}

/** A section, and the floor it sits on. */
export interface LayoutRoom {
  id: string;
  name_en: string;
  name_ar: string;
  sort_order: number;
  /** 0 = ground, negative = below ground. */
  floor: number;
  floor_label_en: string | null;
  floor_label_ar: string | null;
}

export interface LayoutTable {
  id: string;
  room_id: string;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  seat_type: string;
  min_party: number;
  max_party: number;
  priority: number;
  is_online_bookable: boolean;
}

export interface LayoutResponse {
  rooms: LayoutRoom[];
  tables: LayoutTable[];
  combinations: Array<{
    id: string;
    label: string;
    min_party: number;
    max_party: number;
    requires_approval: boolean;
    member_ids: string[];
  }>;
}

export interface ReservationRow {
  id: string;
  booking_ref: string;
  starts_at: string;
  ends_at: string;
  party_size: number;
  status: string;
  source: string;
  occasion: string | null;
  special_requests: string | null;
  internal_notes: string | null;

  /** What the guest told us on the booking form (migration 0018). */
  adults: number;
  kids_0_2: number;
  kids_3_6: number;
  kids_7_12: number;
  seating_preference: string | null;
  /** A table they asked for. A request for staff, never an assignment. */
  preferred_table: string | null;
  heard_about_us: string | null;
  company_name: string | null;
  corporate_discount_pct: string | null;

  version: number;
  guest: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    name_ar: string | null;
    email: string | null;
    phone_e164: string | null;
    vip_level: number;
    allergies: string | null;
    dietary: string[] | null;
    tags: string[] | null;
  } | null;
}

export interface AvailabilitySlot {
  starts_at: string;
  ends_at: string;
  clock: string;
  shift_id: string;
  shift_name: string;
  bookable: boolean;
  confidence: 'open' | 'limited' | 'waitlist_only';
  blocked_by?: string;
}

export interface GuestSummary {
  id: string;
  first_name: string | null;
  last_name: string | null;
  name_ar: string | null;
  email: string | null;
  phone_e164: string | null;
  vip_level: number;
  tags: string[] | null;
  allergies: string | null;
  total_visits: number;
  no_show_count: number;
  last_visit_at: string | null;
}

export interface DuplicateCandidate {
  guest_id: string;
  score: number;
  confident: boolean;
  reasons: string[];
  first_name: string | null;
  last_name: string | null;
  name_ar: string | null;
}

export interface WaitlistEntry {
  id: string;
  party_size: number;
  quoted_wait_minutes: number | null;
  joined_at: string;
  notified_at: string | null;
  seated_at: string | null;
  status: string;
  notes: string | null;
  first_name: string | null;
  last_name: string | null;
  phone_e164: string | null;
}

export interface InboxMessage {
  id: string;
  body_rendered: string;
  created_at: string;
  handled_at: string | null;
  interactive_reply_id: string | null;
  guest_id: string | null;
  first_name: string | null;
  last_name: string | null;
  booking_ref: string | null;
  can_reply_freely: boolean;
}
